import { useEffect, useState } from "react";
import { ordersAnalyticsApi } from "@/features/orders-analytics/api/orders-analytics.api";
import {
    CustomerIntelligenceResponse,
    OrdersAnalyticsFilters,
    OrdersSegmentationResponse,
    SegmentationDimension,
} from "@/features/orders-analytics/types/orders-analytics.types";

// Only the dimensions the view-model actually renders. withdrawProvider /
// onrampProvider are deliberately NOT fetched: their segment values are raw
// vendor names (PII) and the provider ranking is permanently empty-stated, so
// requesting them would pull PII we never display.
const CORRIDOR_DIMENSIONS: SegmentationDimension[] = ["pair", "client"];

export type CorridorsData = {
    segmentation: Partial<Record<SegmentationDimension, OrdersSegmentationResponse>>;
    customerIntelligence: CustomerIntelligenceResponse | null;
};

const EMPTY_DATA: CorridorsData = { segmentation: {}, customerIntelligence: null };

export function useCorridorsData(filters: OrdersAnalyticsFilters) {
    const [data, setData] = useState<CorridorsData>(EMPTY_DATA);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);

        Promise.allSettled([
            ordersAnalyticsApi.fetchSegmentationByDimensions(filters, CORRIDOR_DIMENSIONS),
            ordersAnalyticsApi.fetchCustomerIntelligence(filters),
        ]).then(([segmentationResult, intelligenceResult]) => {
            if (cancelled) return;

            setData({
                segmentation: segmentationResult.status === "fulfilled" ? segmentationResult.value : {},
                customerIntelligence:
                    intelligenceResult.status === "fulfilled" ? intelligenceResult.value : null,
            });
            setLoading(false);
        });

        return () => {
            cancelled = true;
        };
    }, [filters]);

    return { data, loading };
}
